import React, { useCallback, useMemo, useState, JSX, useEffect } from 'react';
import { Button, Dropdown, DropdownMenu, DropdownToggle, Table } from 'reactstrap';
import fallback from '../../../_Translations/generated/simple-list.en.t.json';
import Keys from '../../../_Translations/generated/simple-list.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import { SimpleListDropdownItem } from './additionals';
import { ListRowItem, SimpleListProps, Sorting } from './types';
import { TableBody, TableBodySmallest } from './TableBody';
import TableHead from './TableHead';

const initialDisplayedItemCount = 7;

const SimpleList = <L extends ListRowItem>(props: SimpleListProps<L>): JSX.Element => {
  const {
    listRows,
    headItems,
    itemsInRowInMobile,
    itemsInRowInDesktop,
    customDesktopBodyClass,
    customMobileBodyClass,
    onSelect,
    initialSorting,
    onSort,
    controlledSorting,
    staticNonTableContent,
    checkAvailability,
    errorRowColSpanDiff
  } = props;
  const { t } = useTranslation('simple-list', fallback, Keys);
  const [internalSorting, setCurrentSorting] = useState<Sorting>(
    initialSorting || {
      sortProp: headItems?.find(item => !!item.sortProp)?.sortProp || '',
      isAscDirection: true
    }
  );
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const currentSorting = useMemo(() => controlledSorting || internalSorting, [
    controlledSorting,
    internalSorting
  ]);
  const [showAllItems, setShowAllItems] = useState<boolean>(false);

  const segmentedListRows = useMemo(() => {
    return showAllItems ? listRows : listRows.slice(0, initialDisplayedItemCount);
  }, [listRows, showAllItems]);

  const handleSelect = useCallback(
    (id: string, code: string, index?: number): void => {
      onSelect(id, code, index);
    },
    [onSelect]
  );

  const handleSortClick = useCallback(
    (sortProp: string, isAscDirection?: boolean): void => {
      const newSorting = {
        sortProp,
        isAscDirection:
          isAscDirection !== undefined
            ? isAscDirection
            : sortProp === currentSorting.sortProp
            ? !currentSorting.isAscDirection
            : true
      };
      if (!controlledSorting) {
        setCurrentSorting(newSorting);
      }
      if (onSort) {
        onSort(newSorting);
      }
    },
    [currentSorting, onSort]
  );

  const handleShowMore = useCallback(() => {
    setShowAllItems(true);
  }, []);

  const [desktop, setDesktop] = useState<boolean>(false);
  const [mobile, setMobile] = useState<boolean>(false);
  const [smallest, setSmallest] = useState<boolean>(true);

  const checkViewType = () => {
    setDesktop(window.innerWidth >= 1175);
    setMobile(window.innerWidth < 1175 && window.innerWidth >= 920);
    setSmallest(window.innerWidth < 920);
  };

  useEffect(() => {
    checkViewType();
    window.addEventListener('resize', checkViewType);
    return () => {
      window.removeEventListener('resize', checkViewType);
    };
  }, [window.innerWidth]);

  return (
    <div className="simple-list">
      <Dropdown
        className={`simple-list__sort-dropdown${
          dropdownOpen ? ' simple-list__sort-dropdown--open' : ''
        }`}
        isOpen={dropdownOpen}
        toggle={(): void => setDropdownOpen(value => !value)}
      >
        <DropdownToggle caret>{t(Keys.sorting)}</DropdownToggle>
        <DropdownMenu>
          {headItems
            .filter(headItem => !!headItem.sortProp)
            .map(headItem => (
              <span key={headItem.value}>
                <SimpleListDropdownItem
                  headItem={headItem}
                  sortString={t(Keys.ascending)}
                  currentSorting={currentSorting}
                  handleSortClick={handleSortClick}
                  isAscDirection={true}
                />
                <SimpleListDropdownItem
                  headItem={headItem}
                  sortString={t(Keys.descending)}
                  currentSorting={currentSorting}
                  handleSortClick={handleSortClick}
                  isAscDirection={false}
                />
              </span>
            ))}
        </DropdownMenu>
      </Dropdown>
      <Table striped>
        <TableHead
          headItems={headItems}
          currentSorting={currentSorting}
          onSortClick={handleSortClick}
        />
        {!staticNonTableContent ? (
          <>
            {desktop && (
              <TableBody
                className={`simple-list__body--desktop ${customDesktopBodyClass || ''}`}
                listRows={segmentedListRows}
                itemsInRow={itemsInRowInDesktop}
                onSelect={handleSelect}
                checkAvailability={checkAvailability}
                errorRowColSpanDiff={errorRowColSpanDiff}
                isDesktop
              />
            )}
            {mobile && (
              <TableBody
                className={`simple-list__body--mobile ${customMobileBodyClass || ''}`}
                listRows={segmentedListRows}
                itemsInRow={itemsInRowInMobile}
                onSelect={handleSelect}
                checkAvailability={checkAvailability}
                errorRowColSpanDiff={errorRowColSpanDiff}
              />
            )}
            {smallest && (
              <TableBodySmallest
                className="simple-list__body--smallest"
                listRows={segmentedListRows}
                onSelect={onSelect}
                checkAvailability={checkAvailability}
              />
            )}
          </>
        ) : (
          <></>
        )}
      </Table>
      {!!staticNonTableContent ? staticNonTableContent : <></>}
      {!showAllItems && listRows.length > initialDisplayedItemCount && (
        <div className="simple-list__show-more mt-6 d-flex justify-content-center">
          <Button color="secondary" onClick={handleShowMore}>
            {t(Keys.showFurtherElements)}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SimpleList;
